import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  Chip,
  Stack,
  Divider,
  useTheme,
  InputAdornment,
  LinearProgress,
  Avatar,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as CartIcon,
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  AccountBalanceWallet as WalletIcon,
  Verified as VerifiedIcon,
  LocationCity as RealEstateIcon,
  Pets as LivestockIcon,
  Spa as GoldIcon,
  Business as CommercialIcon,
  Home as ResidentialIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import {
  getAllAssets,
  purchaseTokens,
  getTokenPrices,
  getAssetPrice,
  setSelectedAssetForPurchase,
  clearPurchaseResult,
  AssetDto,
  AssetStatus,
  TokenPriceDto,
} from "../../redux/assetSlice";
import { RootState } from "../../redux/store";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

interface AssetCardProps {
  asset: AssetDto;
  price?: number;
  priceChange?: number;
  onPurchase: (asset: AssetDto) => void;
  isMobile: boolean;
}

const getAssetTypeName = (asset: AssetDto): string => {
  switch (asset.assetType) {
    case 1:
      return "Real Estate";
    case 2:
      return "Livestock";
    case 3:
      return "Gold";
    case 4:
      return "Silver";
    case 5:
      return "Copper";
    case 6:
      return "Other";
    default:
      return "Unknown";
  }
};

const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  price,
  priceChange,
  onPurchase,
  isMobile,
}) => {
  const theme = useTheme();

  // Get icon based on asset type
  const getAssetIcon = () => {
    let typeName = getAssetTypeName(asset);
    switch (typeName.toLowerCase()) {
      case "realestate":
      case "commercial":
        return <CommercialIcon />;
      case "residential":
        return <ResidentialIcon />;
      case "livestock":
        return <LivestockIcon />;
      case "gold":
        return <GoldIcon />;
      default:
        return <RealEstateIcon />;
    }
  };

  // Get status color
  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.Verified:
      case AssetStatus.Active:
        return "success";
      case AssetStatus.Draft:
        return "default";
      case AssetStatus.Minting:
        return "warning";
      case AssetStatus.Suspended:
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        mb: 2,
        "&:last-child": { mb: 0 },
      }}
    >
      <Card
        sx={{
          width: "100%",
          borderRadius: 2,
          boxShadow: 1,
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow: 3,
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
            {/* Asset Icon */}
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 50,
                height: 50,
              }}
            >
              {getAssetIcon()}
            </Avatar>

            {/* Asset Info */}
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? "0.9rem" : "1rem",
                    mr: 1,
                  }}
                >
                  {asset.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                  }}
                >
                  {asset.symbol}
                </Typography>
                {asset.isVerified && (
                  <VerifiedIcon
                    color="success"
                    fontSize="small"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>

              {/* Asset Description */}
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: isMobile ? "0.8rem" : "0.875rem",
                  mb: 2,
                  lineHeight: 1.4,
                }}
              >
                {asset.assetDetails.length > 80
                  ? `${asset.assetDetails.substring(0, 80)}...`
                  : asset.assetDetails}
              </Typography>

              {/* Asset Details Row */}
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Total Supply
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {asset.totalSupply.toLocaleString()}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", display: "block" }}
                  >
                    Token Price
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {price ? `$${price.toFixed(2)}` : "Loading..."}
                  </Typography>
                </Box>
                {priceChange !== undefined && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", display: "block" }}
                    >
                      24h Change
                    </Typography>
                    <Chip
                      size="small"
                      icon={
                        priceChange >= 0 ? (
                          <TrendingUpIcon fontSize="small" />
                        ) : (
                          <TrendingDownIcon fontSize="small" />
                        )
                      }
                      label={`${
                        priceChange >= 0 ? "+" : ""
                      }${priceChange.toFixed(2)}%`}
                      color={priceChange >= 0 ? "success" : "error"}
                      variant="outlined"
                      sx={{ height: 24 }}
                    />
                  </Box>
                )}
              </Box>

              {/* Status and Contract Info */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={AssetStatus[asset.status]}
                  size="small"
                  color={getStatusColor(asset.status)}
                  sx={{ fontSize: isMobile ? "0.7rem" : "0.75rem" }}
                />
                {asset.contractAddress && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontSize: isMobile ? "0.7rem" : "0.75rem",
                    }}
                  >
                    Contract:{" "}
                    {`${asset.contractAddress.substring(
                      0,
                      6
                    )}...${asset.contractAddress.substring(38)}`}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>

        <CardActions
          sx={{
            p: 2,
            pt: 0,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            startIcon={<CartIcon />}
            onClick={() => onPurchase(asset)}
            disabled={
              asset.status !== AssetStatus.Verified &&
              asset.status !== AssetStatus.Active
            }
            size={isMobile ? "small" : "medium"}
            sx={{
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? "0.8rem" : "0.875rem",
            }}
          >
            {asset.status === AssetStatus.Verified ||
            asset.status === AssetStatus.Active
              ? "Buy Tokens"
              : "Not Available"}
          </Button>
        </CardActions>
      </Card>
    </Box>
  );
};

interface PurchasePanelProps {
  asset: AssetDto | null;
  price?: number;
  onClose: () => void;
  onConfirm: (
    amount: number,
    paymentMethod: string,
    walletAddress?: string
  ) => void;
  loading: boolean;
  isMobile: boolean;
}

const PurchasePanel: React.FC<PurchasePanelProps> = ({
  asset,
  price,
  onClose,
  onConfirm,
  loading,
  isMobile,
}) => {
  const theme = useTheme();
  const [amount, setAmount] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>("wallet");
  const [walletAddress, setWalletAddress] = useState<string>("");
  const [errors, setErrors] = useState<{
    amount?: string;
    walletAddress?: string;
  }>({});

  useEffect(() => {
    if (asset) {
      setAmount("");
      setPaymentMethod("wallet");
      setWalletAddress("");
      setErrors({});
    }
  }, [asset]);

  const validateForm = () => {
    const newErrors: { amount?: string; walletAddress?: string } = {};

    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = "Please enter a valid amount";
    } else if (asset && parseFloat(amount) > asset.totalSupply) {
      newErrors.amount = "Amount exceeds available supply";
    }

    if (paymentMethod === "wallet" && !walletAddress) {
      newErrors.walletAddress = "Wallet address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = () => {
    if (validateForm() && asset) {
      onConfirm(
        parseFloat(amount),
        paymentMethod,
        paymentMethod === "wallet" ? walletAddress : undefined
      );
    }
  };

  const calculateTotalCost = () => {
    if (!amount || !price) return 0;
    return parseFloat(amount) * price;
  };

  const getPaymentMethodIcon = () => {
    switch (paymentMethod) {
      case "wallet":
        return <WalletIcon />;
      case "card":
        return <CardIcon />;
      case "bank":
        return <BankIcon />;
      default:
        return <MoneyIcon />;
    }
  };

  if (!asset) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1300,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: isMobile ? 1 : 2,
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: isMobile ? "100%" : "500px",
          maxHeight: isMobile ? "100vh" : "90vh",
          overflow: "auto",
          borderRadius: isMobile ? 0 : 2,
          boxShadow: 24,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              sx={{
                bgcolor: theme.palette.primary.main,
                width: 36,
                height: 36,
              }}
            >
              <CartIcon />
            </Avatar>
            <Typography variant="h6">Purchase {asset.name} Tokens</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 2 }}>
          <Stack spacing={3}>
            {/* Asset Info */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {asset.name} ({asset.symbol})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {asset.assetType}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="h6" color="primary">
                    ${price?.toFixed(2) || "0.00"}
                  </Typography>
                  <Typography variant="caption">per token</Typography>
                </Box>
              </Box>
            </Paper>

            {/* Amount Input */}
            <Box>
              <TextField
                fullWidth
                label="Token Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                error={!!errors.amount}
                helperText={errors.amount}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography variant="caption">{asset.symbol}</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  min: 1,
                  max: asset.totalSupply,
                  step: 1,
                }}
                size={isMobile ? "small" : "medium"}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                Available: {asset.totalSupply.toLocaleString()} {asset.symbol}
              </Typography>
            </Box>

            {/* Total Cost */}
            {amount && price && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 0.5,
                  }}
                >
                  <Typography>Total Cost:</Typography>
                  <Typography variant="h6" color="primary">
                    ${calculateTotalCost().toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {amount} tokens × ${price.toFixed(2)} each
                </Typography>
              </Paper>
            )}

            {/* Payment Method */}
            <Box>
              <FormControl fullWidth>
                <InputLabel size={isMobile ? "small" : "medium"}>
                  Payment Method
                </InputLabel>
                <Select
                  value={paymentMethod}
                  onChange={(e: SelectChangeEvent) =>
                    setPaymentMethod(e.target.value)
                  }
                  label="Payment Method"
                  size={isMobile ? "small" : "medium"}
                  startAdornment={
                    <InputAdornment position="start">
                      {getPaymentMethodIcon()}
                    </InputAdornment>
                  }
                >
                  <MenuItem value="wallet">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <WalletIcon fontSize="small" />
                      <Typography>Crypto Wallet</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="card">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CardIcon fontSize="small" />
                      <Typography>Credit/Debit Card</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="bank">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <BankIcon fontSize="small" />
                      <Typography>Bank Transfer</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Wallet Address (only for wallet payment) */}
            {paymentMethod === "wallet" && (
              <TextField
                fullWidth
                label="Wallet Address"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                error={!!errors.walletAddress}
                helperText={
                  errors.walletAddress || "Enter your Polygon wallet address"
                }
                placeholder="0x..."
                size={isMobile ? "small" : "medium"}
              />
            )}

            {/* Terms and Conditions */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
            >
              By purchasing tokens, you agree to our Terms of Service and
              confirm that you understand the risks associated with tokenized
              real-world assets.
            </Typography>
          </Stack>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: "flex",
            gap: 1,
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            fullWidth
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={loading || !amount || !price}
            fullWidth
            size={isMobile ? "small" : "medium"}
            sx={{ minWidth: 120 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              `Purchase ${amount || 0} Tokens`
            )}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

const AssetsView: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const dispatch = useAppDispatch();

  // Redux state
  const {
    assets,
    tokenPrices,
    selectedAssetForPurchase,
    purchaseLoading,
    purchaseResult,
    getAllLoading,
    getAllError,
    tokenPriceLoading,
  } = useAppSelector((state: RootState) => state.assets);

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [assetTypeFilter, setAssetTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("name");

  // Fetch assets and prices on mount
  useEffect(() => {
    dispatch(
      getAllAssets({
        skipCount: 0,
        maxResultCount: 50,
      })
    );
    dispatch(getTokenPrices());
  }, [dispatch]);

  // Handle purchase success
  useEffect(() => {
    if (purchaseResult?.success) {
      setShowSuccessSnackbar(true);
      setShowPurchasePanel(false);
      dispatch(clearPurchaseResult());
    }
  }, [purchaseResult, dispatch]);

  // State for purchase panel and notifications
  const [showPurchasePanel, setShowPurchasePanel] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Filter and sort assets
  const filteredAssets = useMemo(() => {
    let filtered = [...assets];

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter(
        (asset) =>
          asset.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          asset.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
          asset.assetDetails.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Apply asset type filter
    if (assetTypeFilter !== "all") {
      filtered = filtered.filter(
        (asset) =>
          getAssetTypeName(asset).toLowerCase() ===
          assetTypeFilter.toLowerCase()
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (asset) => asset.status.toString() === statusFilter
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name);
        case "price":
          const priceA =
            tokenPrices.find((p) => p.assetId === a.id)?.currentPrice || 0;
          const priceB =
            tokenPrices.find((p) => p.assetId === b.id)?.currentPrice || 0;
          return priceB - priceA;
        case "supply":
          return b.totalSupply - a.totalSupply;
        case "recent":
          return (
            new Date(b.creationTime).getTime() -
            new Date(a.creationTime).getTime()
          );
        default:
          return 0;
      }
    });

    return filtered;
  }, [assets, searchQuery, assetTypeFilter, statusFilter, sortBy, tokenPrices]);

  // Handle purchase button click
  const handlePurchaseClick = (asset: AssetDto) => {
    dispatch(setSelectedAssetForPurchase(asset));

    // Fetch latest price for the asset
    dispatch(getAssetPrice(asset.id));

    setShowPurchasePanel(true);
  };

  // Handle purchase confirmation
  const handlePurchaseConfirm = async (
    amount: number,
    paymentMethod: string,
    walletAddress?: string
  ) => {
    if (selectedAssetForPurchase) {
      await dispatch(
        purchaseTokens({
          assetId: selectedAssetForPurchase.id,
          amount,
          paymentMethod: paymentMethod as any,
          walletAddress,
        })
      );
    }
  };

  // Close purchase panel
  const handleClosePurchasePanel = () => {
    setShowPurchasePanel(false);
    dispatch(setSelectedAssetForPurchase(null));
  };

  // Get price for an asset
  const getAssetPriceInfo = (assetId: string) => {
    const priceInfo = tokenPrices.find((p) => p.assetId === assetId);
    return priceInfo;
  };

  // Get unique asset types for filter
  const assetTypes = useMemo(() => {
    const types = new Set<string>();
    assets.forEach((asset) => types.add(getAssetTypeName(asset)));
    return Array.from(types);
  }, [assets]);

  // Scrollable content styles
  const scrollableBoxStyles = {
    maxHeight: isMobile ? "76vh" : "68vh",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    p: isMobile ? 1 : 2,
  };

  return (
    <>
      <Paper
        sx={{
          p: isMobile ? 1 : 2,
          width: "100%",
          maxWidth: MOBILE_APP_WIDTH,
          margin: "auto",
          height: isMobile ? "" : "850px",
          ...(isMobile && {
            maxWidth: "100%",
            borderRadius: 0,
            boxShadow: "none",
          }),
          ...(!isMobile && {
            borderRadius: 2,
            boxShadow: 3,
          }),
        }}
      >
        {/* Header Section */}
        <Box sx={{ p: isMobile ? 1 : 2, textAlign: "start", flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              fontWeight: 600,
              mb: 1,
            }}
          >
            Real World Assets
          </Typography>
          <Typography
            variant="body1"
            gutterBottom
            sx={{
              fontSize: isMobile ? "0.8rem" : "1rem",
              color: "text.secondary",
            }}
          >
            Invest in tokenized real-world assets - Real Estate, Livestock, Gold
          </Typography>
        </Box>
        <Divider />

        {/* Search Box */}
        <Box sx={{ p: isMobile ? 1 : 2 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search assets by name, symbol, or description..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Filter Row */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            {/* Asset Type Filter */}
            <FormControl
              size={isMobile ? "small" : "medium"}
              sx={{ minWidth: 120, flex: 1 }}
            >
              <InputLabel>Asset Type</InputLabel>
              <Select
                value={assetTypeFilter}
                onChange={(e) => setAssetTypeFilter(e.target.value)}
                label="Asset Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                {assetTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status Filter */}
            <FormControl
              size={isMobile ? "small" : "medium"}
              sx={{ minWidth: 120, flex: 1 }}
            >
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value={AssetStatus.Verified.toString()}>
                  Verified
                </MenuItem>
                <MenuItem value={AssetStatus.Active.toString()}>
                  Active
                </MenuItem>
                <MenuItem value={AssetStatus.Draft.toString()}>Draft</MenuItem>
              </Select>
            </FormControl>

            {/* Sort By */}
            <FormControl
              size={isMobile ? "small" : "medium"}
              sx={{ minWidth: 120, flex: 1 }}
            >
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                label="Sort By"
              >
                <MenuItem value="name">Name</MenuItem>
                <MenuItem value="price">Price</MenuItem>
                <MenuItem value="supply">Total Supply</MenuItem>
                <MenuItem value="recent">Recently Added</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Loading Indicator */}
          {getAllLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

          {/* Error Message */}
          {getAllError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => {}}>
              {getAllError}
            </Alert>
          )}
        </Box>

        {/* Assets List */}
        <Box sx={{ ...scrollableBoxStyles }}>
          {filteredAssets.length === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                color: "text.secondary",
              }}
            >
              <TrendingUpIcon
                sx={{
                  fontSize: 60,
                  mb: 2,
                  opacity: 0.5,
                }}
              />
              <Typography variant="h6" gutterBottom>
                No assets found
              </Typography>
              <Typography variant="body2">
                {searchQuery
                  ? "Try a different search term"
                  : "Check back later for new assets"}
              </Typography>
            </Box>
          ) : (
            filteredAssets.map((asset) => {
              const priceInfo = getAssetPriceInfo(asset.id);
              return (
                <AssetCard
                  key={asset.id}
                  asset={asset}
                  price={priceInfo?.currentPrice}
                  priceChange={priceInfo?.priceChange24h}
                  onPurchase={handlePurchaseClick}
                  isMobile={isMobile}
                />
              );
            })
          )}
        </Box>
      </Paper>

      {/* Purchase Panel (conditionally rendered div) */}
      {showPurchasePanel && (
        <PurchasePanel
          asset={selectedAssetForPurchase}
          price={
            getAssetPriceInfo(selectedAssetForPurchase?.id || "")?.currentPrice
          }
          onClose={handleClosePurchasePanel}
          onConfirm={handlePurchaseConfirm}
          loading={purchaseLoading}
          isMobile={isMobile}
        />
      )}

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessSnackbar(false)}
          severity="success"
          sx={{ width: "100%", minWidth: isMobile ? "auto" : 300 }}
          variant="filled"
        >
          Purchase successful!{" "}
          {purchaseResult?.transactionHash && (
            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
              Transaction: {purchaseResult.transactionHash.substring(0, 10)}...
            </Typography>
          )}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      {purchaseResult?.error && (
        <Snackbar
          open={!purchaseResult.success && !!purchaseResult.error}
          autoHideDuration={6000}
          onClose={() => dispatch(clearPurchaseResult())}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => dispatch(clearPurchaseResult())}
            severity="error"
            sx={{ width: "100%", minWidth: isMobile ? "auto" : 300 }}
            variant="filled"
          >
            Purchase failed: {purchaseResult.error}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AssetsView;
